import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Keyboard, Play, Clock, Target, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEST_DURATION = 300; // 5 minutes in seconds

export default function TypingTestPage() {
  const { participantId } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const textDisplayRef = useRef(null);
  const currentCharRef = useRef(null);
  
  const [text, setText] = useState("");
  const [userInput, setUserInput] = useState("");
  const [started, setStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(TEST_DURATION);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchText();
  }, []);

  useEffect(() => {
    let timer;
    if (started && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [started, timeLeft]);

  const fetchText = async () => {
    try {
      const response = await axios.get(`${API}/typing-test/text`);
      setText(response.data.text);
    } catch (error) {
      toast.error("Gagal memuat teks");
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    setStarted(true);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    if (!started || timeLeft === 0) return;
    setUserInput(e.target.value);
  };

  // Auto-scroll to current character
  useEffect(() => {
    if (currentCharRef.current && textDisplayRef.current) {
      currentCharRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, [userInput]);

  const calculateResults = useCallback(() => {
    const typedChars = userInput.length;
    let correctChars = 0;
    
    for (let i = 0; i < typedChars; i++) {
      if (userInput[i] === text[i]) {
        correctChars++;
      }
    }
    
    const timeSpent = TEST_DURATION - timeLeft;
    const minutes = timeSpent / 60;
    
    // WPM = (characters typed / 5) / minutes
    const wpm = minutes > 0 ? (typedChars / 5) / minutes : 0;
    const accuracy = typedChars > 0 ? (correctChars / typedChars) * 100 : 0;
    
    return {
      wpm: Math.round(wpm * 10) / 10,
      accuracy: Math.round(accuracy * 10) / 10,
      totalCharacters: typedChars,
      correctCharacters: correctChars,
      timeSpent: timeSpent
    };
  }, [userInput, text, timeLeft]);

  const handleSubmit = async () => {
    if (submitting) return;
    
    setSubmitting(true);
    const results = calculateResults();
    
    try {
      await axios.post(`${API}/typing-test/submit`, {
        participant_id: participantId,
        wpm: results.wpm,
        accuracy: results.accuracy,
        total_characters: results.totalCharacters,
        correct_characters: results.correctCharacters,
        time_spent: results.timeSpent
      });
      
      toast.success("Test selesai!");
      navigate(`/results/${participantId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan hasil");
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderText = () => {
    return text.split('').map((char, idx) => {
      let className = "typing-char ";
      const isCurrent = idx === userInput.length;
      
      if (idx < userInput.length) {
        if (userInput[idx] === char) {
          className += "correct";
        } else {
          className += "incorrect";
        }
      } else if (isCurrent) {
        className += "current";
      } else {
        className += "untyped";
      }
      
      return (
        <span 
          key={idx} 
          className={className}
          ref={isCurrent ? currentCharRef : null}
        >
          {char}
        </span>
      );
    });
  };

  const currentResults = calculateResults();
  const progress = ((TEST_DURATION - timeLeft) / TEST_DURATION) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Keyboard className="w-12 h-12 text-[#002FA7] mx-auto mb-4 animate-pulse" />
          <p className="text-[#475569]">Memuat teks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Keyboard className="w-5 h-5 text-[#002FA7]" />
              <span className="font-bold text-[#0A0A0A]">Test Kecepatan Mengetik</span>
            </div>
            <div 
              className={`font-mono text-xl font-bold ${timeLeft <= 60 ? 'text-red-500' : 'text-[#002FA7]'}`}
              data-testid="timer"
            >
              <Clock className="w-4 h-4 inline mr-1" />
              {formatTime(timeLeft)}
            </div>
          </div>
          {started && <Progress value={progress} className="h-1" />}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {!started ? (
          /* Pre-start Screen */
          <div 
            className="bg-white border border-slate-200 p-8 md:p-12 text-center animate-fadeIn"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.95), rgba(255,255,255,0.95)), url('https://static.prod-images.emergentagent.com/jobs/318fca79-6275-404f-b8c1-cb550d4c33df/images/35993adbf263802746064d74b1cb1170be6dd70730d7eb375343aa6f95f537f0.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <Keyboard className="w-16 h-16 text-[#002FA7] mx-auto mb-6" />
            <h1 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4">
              Test Kecepatan Mengetik
            </h1>
            <p className="text-[#475569] text-lg mb-8 max-w-md mx-auto">
              Anda memiliki waktu <span className="font-bold text-[#002FA7]">5 menit</span> untuk mengetik 
              teks yang ditampilkan. Ketik secepat dan seakurat mungkin.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 text-[#475569]">
                <Clock className="w-5 h-5 text-[#002FA7]" />
                <span>5 Menit</span>
              </div>
              <div className="flex items-center gap-2 text-[#475569]">
                <Target className="w-5 h-5 text-[#002FA7]" />
                <span>Akurasi Dihitung</span>
              </div>
              <div className="flex items-center gap-2 text-[#475569]">
                <Zap className="w-5 h-5 text-[#002FA7]" />
                <span>WPM Dihitung</span>
              </div>
            </div>

            <Button
              onClick={handleStart}
              size="lg"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white font-semibold rounded-sm px-8 h-14 text-lg animate-pulse-glow"
              data-testid="start-typing-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Mulai Test
            </Button>
          </div>
        ) : (
          /* Typing Test Interface */
          <div className="space-y-6 animate-fadeIn">
            {/* Stats Bar */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-slate-200 p-4 text-center">
                <Zap className="w-5 h-5 text-[#002FA7] mx-auto mb-1" />
                <p className="font-mono text-2xl font-bold text-[#0A0A0A]" data-testid="wpm-display">
                  {currentResults.wpm}
                </p>
                <p className="text-xs text-[#475569] uppercase tracking-wider">WPM</p>
              </div>
              <div className="bg-white border border-slate-200 p-4 text-center">
                <Target className="w-5 h-5 text-[#002FA7] mx-auto mb-1" />
                <p className="font-mono text-2xl font-bold text-[#0A0A0A]" data-testid="accuracy-display">
                  {currentResults.accuracy}%
                </p>
                <p className="text-xs text-[#475569] uppercase tracking-wider">Akurasi</p>
              </div>
              <div className="bg-white border border-slate-200 p-4 text-center">
                <Keyboard className="w-5 h-5 text-[#002FA7] mx-auto mb-1" />
                <p className="font-mono text-2xl font-bold text-[#0A0A0A]" data-testid="chars-display">
                  {currentResults.totalCharacters}
                </p>
                <p className="text-xs text-[#475569] uppercase tracking-wider">Karakter</p>
              </div>
            </div>

            {/* Text Display */}
            <div className="bg-white border border-slate-200 p-6 md:p-8 max-h-64 overflow-y-auto" ref={textDisplayRef}>
              <p className="text-xs text-[#475569] uppercase tracking-wider mb-4">
                Ketik teks berikut:
              </p>
              <div 
                className="font-mono text-lg leading-loose text-[#0A0A0A] whitespace-pre-wrap select-none"
                data-testid="text-display"
              >
                {renderText()}
              </div>
            </div>

            {/* Input Area */}
            <div className="relative">
              <textarea
                ref={inputRef}
                value={userInput}
                onChange={handleInputChange}
                disabled={timeLeft === 0}
                autoFocus
                className="w-full h-40 p-6 font-mono text-lg bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-none"
                placeholder="Mulai mengetik di sini..."
                data-testid="typing-input"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={submitting || userInput.length === 0}
                className="bg-[#002FA7] hover:bg-[#00237C] rounded-sm"
                data-testid="submit-typing-btn"
              >
                {submitting ? "Menyimpan..." : "Selesai & Lihat Hasil"}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
